import { useTranslation } from "react-i18next";
import {
  Accordion,
  Button,
  FormInput,
  FormTitle,
  HeadlessTable,
  StatusIndicator,
  Tooltip,
} from "@/shared/components";
import { DelegateOf, RepresentationMode } from "@/shared/types/user";
import { useCallback, useMemo, useState } from "react";
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import {
  getPersonDisplayName,
  getPreferredIdentifier,
  getTIN,
  getTypeOfIdentifier,
} from "@/utils";
import { useBusinessEntities } from "@/hooks/useBusinessEntityQueries";
import { UiBusinessEntityLookup } from "@/generated/ui-api/types.gen";

interface RepresentedEntityFormProps {
  onCancel?: () => void;
  onSelect?: () => void;
  delegateList: DelegateOf[];
  currentDelegate?: DelegateOf | null;
  setDelegation(
    delegateOf: DelegateOf | null | undefined,
    mode: RepresentationMode
  ): Promise<void>;
}

export function RepresentedEntityForm({
  onSelect,
  delegateList,
  currentDelegate,
  setDelegation,
}: RepresentedEntityFormProps) {
  const { t } = useTranslation();
  const [globalFilter, setGlobalFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [obligationFilter, setObligationFilter] = useState<string>("");
  const [appliedObligationFilter, setAppliedObligationFilter] =
    useState<string>("");

  const [directPagination, setDirectPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  const [delegatedPagination, setDelegatedPagination] =
    useState<PaginationState>({
      pageIndex: 0,
      pageSize: 5,
    });

  const validDelegates = useMemo(() => {
    return delegateList.filter(delegate => !!getPreferredIdentifier(delegate));
  }, [delegateList]);

  const lookups: UiBusinessEntityLookup[] = useMemo(
    () =>
      validDelegates
        .map(delegate => {
          const identifier = getPreferredIdentifier(delegate);
          if (identifier) {
            return {
              businessEntityIdentifier: identifier.tin,
              businessEntityIdentifierType: identifier.typeOfIdentifier,
            };
          }
          return null;
        })
        .filter((lookup): lookup is UiBusinessEntityLookup => lookup !== null),
    [validDelegates]
  );

  const { data: businessEntities } = useBusinessEntities(lookups);

  const enrichedDelegateList = useMemo(() => {
    if (!businessEntities) return validDelegates;

    return validDelegates.map(delegate => {
      const identifier = getPreferredIdentifier(delegate);
      const tin = identifier?.tin;
      const be = businessEntities.find(b => b.businessEntityIdentifier === tin);
      if (be) {
        return {
          ...delegate,
          hasReportingObligations: be.hasReportingObligations,
          hasLateObligations: be.hasLateObligations,
        };
      }
      return delegate;
    });
  }, [validDelegates, businessEntities]);

  const filteredDelegateList = useMemo(() => {
    return enrichedDelegateList.filter(delegate => {
      if (appliedObligationFilter === "reporting") {
        return !!delegate.hasReportingObligations;
      }
      if (appliedObligationFilter === "late") {
        return !!delegate.hasLateObligations;
      }
      return true;
    });
  }, [enrichedDelegateList, appliedObligationFilter]);

  const directDelegates = useMemo(() => {
    return filteredDelegateList.filter(d => d.hasDirectBP);
  }, [filteredDelegateList]);

  const delegatedDelegates = useMemo(() => {
    return filteredDelegateList.filter(d => d.hasDelegatedBP);
  }, [filteredDelegateList]);

  const handleFilter = () => {
    setGlobalFilter(searchInput);
    setAppliedObligationFilter(obligationFilter);
    setDirectPagination(prev => ({ ...prev, pageIndex: 0 }));
    setDelegatedPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const handleClearFilter = () => {
    setSearchInput("");
    setGlobalFilter("");
    setObligationFilter("");
    setAppliedObligationFilter("");
    setDirectPagination(prev => ({ ...prev, pageIndex: 0 }));
    setDelegatedPagination(prev => ({ ...prev, pageIndex: 0 }));
  };

  const getRowClassName = (person: DelegateOf) => {
    const isSelected = currentDelegate?.id === person.id;
    return isSelected ? "bg-blue-50" : "";
  };

  const basicColumns = useMemo<ColumnDef<DelegateOf>[]>(
    () => [
      {
        accessorFn: row => getPersonDisplayName(row),
        id: "name",
        header: () => t("forms.representedEntity.columns.name"),
        cell: info => info.getValue(),
        size: 250,
      },
      {
        accessorFn: row => getTIN(row),
        id: "tin",
        header: () => t("forms.representedEntity.columns.tin"),
        cell: info => info.getValue(),
        size: 180,
      },
      {
        accessorFn: row => getTypeOfIdentifier(row),
        id: "typeOfIdentifier",
        header: () => t("forms.representedEntity.columns.typeOfIdentifier"),
        cell: info => info.getValue(),
        size: 220,
      },
    ],
    [t]
  );

  const obligationColumns = useMemo<ColumnDef<DelegateOf>[]>(
    () => [
      {
        accessorKey: "hasReportingObligations",
        header: () =>
          t("forms.representedEntity.columns.hasReportingObligations"),
        cell: info => (
          <div className="flex justify-center items-center h-full">
            {(info.getValue() as boolean) && (
              <Tooltip
                content={t(
                  "forms.representedEntity.tooltips.hasReportingObligations"
                )}
              >
                <div className="flex items-center">
                  <StatusIndicator status="neutral" size="lg" />
                </div>
              </Tooltip>
            )}
          </div>
        ),
        size: 150,
      },
      {
        accessorKey: "hasLateObligations",
        header: () => t("forms.representedEntity.columns.hasLateObligations"),
        cell: info => (
          <div className="flex justify-center items-center h-full">
            {(info.getValue() as boolean) && (
              <Tooltip
                content={t(
                  "forms.representedEntity.tooltips.hasLateObligations"
                )}
                variant="error"
              >
                <div className="flex items-center">
                  <StatusIndicator status="error" size="lg" />
                </div>
              </Tooltip>
            )}
          </div>
        ),
        size: 150,
      },
    ],
    [t]
  );

  const getActionColumn = useCallback(
    (mode: RepresentationMode): ColumnDef<DelegateOf> => ({
      id: "actions",
      header: "",
      cell: info => (
        <div className="flex justify-end items-center h-full">
          <Button
            variant="secondary"
            size="md"
            onClick={async () => {
              await setDelegation(info.row.original, mode);
              onSelect?.();
            }}
          >
            {t("common.select")}
          </Button>
        </div>
      ),
      size: 120,
    }),
    [t, setDelegation, onSelect]
  );

  const commonColumns = useMemo<ColumnDef<DelegateOf>[]>(
    () => [...basicColumns, ...obligationColumns],
    [basicColumns, obligationColumns]
  );

  const directColumns = useMemo(
    () => [...commonColumns, getActionColumn("DIRECT")],
    [commonColumns, getActionColumn]
  );

  const delegatedColumns = useMemo<ColumnDef<DelegateOf>[]>(
    () => [
      ...basicColumns,
      {
        accessorKey: "brokerName",
        header: () => t("forms.representedEntity.columns.brokerName"),
        cell: info => info.getValue() || "-",
        size: 250,
      },
      {
        accessorKey: "brokerTin",
        header: () => t("forms.representedEntity.columns.brokerTin"),
        cell: info => info.getValue() || "-",
        size: 180,
      },
      ...obligationColumns,
      getActionColumn("DELEGATED"),
    ],
    [basicColumns, obligationColumns, getActionColumn, t]
  );

  const directTable = useReactTable({
    data: directDelegates,
    columns: directColumns,
    state: {
      globalFilter,
      pagination: directPagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setDirectPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const delegatedTable = useReactTable({
    data: delegatedDelegates,
    columns: delegatedColumns,
    state: {
      globalFilter,
      pagination: delegatedPagination,
    },
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setDelegatedPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="relative">
      <FormTitle
        title={t("forms.representedEntity.title")}
        className="text-4xl"
      />
      <div className="mt-6 flex flex-col gap-6">
        <Accordion title={t("forms.representedEntity.search")}>
          <form
            onSubmit={e => {
              e.preventDefault();
              handleFilter();
            }}
            className="space-y-4 pt-4"
          >
            <div className="flex flex-col gap-4">
              <FormInput
                label={t("forms.representedEntity.search")}
                value={searchInput}
                onChange={setSearchInput}
                placeholder={t("forms.representedEntity.searchPlaceholder")}
                className="max-w-md"
              />

              <div>
                <div className="flex flex-wrap gap-x-8 gap-y-2 h-10 items-center">
                  {[
                    {
                      label: t("reportItemsPage.filters.all"),
                      value: "",
                    },
                    {
                      label: t(
                        "forms.representedEntity.columns.hasReportingObligations"
                      ),
                      value: "reporting",
                    },
                    {
                      label: t(
                        "forms.representedEntity.columns.hasLateObligations"
                      ),
                      value: "late",
                    },
                  ].map(option => (
                    <label
                      key={option.value}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        checked={obligationFilter === option.value}
                        onChange={() => setObligationFilter(option.value)}
                      />
                      <span className="text-sm text-gray-700">
                        {option.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-start space-x-3 border-t border-gray-100 pt-4">
              <Button type="submit" variant="primary" size="md">
                {t("reportItemsPage.filters.filter")}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={handleClearFilter}
              >
                {t("common.clearFilter")}
              </Button>
            </div>
          </form>
        </Accordion>

        <div className="flex flex-col gap-8">
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-gray-900 px-1">
              {t("forms.representedEntity.directTitle")}
            </h2>
            <HeadlessTable
              table={directTable}
              rowClassName={getRowClassName}
              pagination={{
                currentPage: directPagination.pageIndex + 1,
                totalPages: directTable.getPageCount(),
                totalItems: directTable.getFilteredRowModel().rows.length,
                pageSize: directPagination.pageSize,
              }}
              onPageChange={page =>
                setDirectPagination(prev => ({ ...prev, pageIndex: page - 1 }))
              }
            />
          </div>

          {delegatedDelegates.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-900 px-1">
                {t("forms.representedEntity.delegatedTitle")}
              </h2>
              <div className="overflow-x-auto">
                <HeadlessTable
                  table={delegatedTable}
                  rowClassName={getRowClassName}
                  pagination={{
                    currentPage: delegatedPagination.pageIndex + 1,
                    totalPages: delegatedTable.getPageCount(),
                    totalItems:
                      delegatedTable.getFilteredRowModel().rows.length,
                    pageSize: delegatedPagination.pageSize,
                  }}
                  onPageChange={page =>
                    setDelegatedPagination(prev => ({
                      ...prev,
                      pageIndex: page - 1,
                    }))
                  }
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
